import { Box, Chip, IconButton, CircularProgress, Grid, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import Iconify from "../../../../../reusable-components/iconify";
import { sendTicketMessage } from "../../../../../redux/slices/tickets";
import { useSelector } from "../../../../../redux/store";
import OptionsButton from "./OptionsButton";
import MobileTicketInputEditor from "./MobileTicketInputEditor";
import { useSettingsCheck } from "../../../../../reusable-components/settings";
import FileAttachment from "../../../../../reusable-components/file-attachment/FileAttachment";

MobileTicketMessageInput.propTypes = {
  users: PropTypes.array,
  messagesRef: PropTypes.object,
  setIsTicketInfoOpen: PropTypes.func,
  setIsAddingSubticket: PropTypes.func,
};
export default function MobileTicketMessageInput({users, messagesRef, setIsTicketInfoOpen, setIsAddingSubticket}) {
  const [message, setMessage] = useState('');
  const location = useLocation();
  const subticket = new URLSearchParams(location.search).get('subticket');
  const { id = '' } = useParams();
  const [attachments, setAttachments] = useState([]);
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const { users: possibleUsers, mainTicket, subtickets } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || [];
  const currentTicketData = subticket ? subtickets?.find((st) => st.id === parseInt(subticket, 10)) : mainTicket;
  const rawSuggestedResponses = currentTicketData?.suggestedResponses;
  const suggestedResponses = [...(rawSuggestedResponses || [])].sort((a, b) => (a.text?.length || 0) - (b.text?.length || 0));
  const [suggestedResponseUsed, setSuggestedResponseUsed] = useState(false);
  const [prependText, setPrependText] = useState(null);
  useEffect(() => {
    setSuggestedResponseUsed(false);
  }, [rawSuggestedResponses]);

  const signature = useSettingsCheck('AutoSignatureOnTickets');
  const defaultMessage = signature?.show ? signature?.value : '';

  const removeAttachment = (fileToRemove) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((file) => file !== fileToRemove)
    );
  };

  const scrollToBottom = () => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }

  const sendMessage = async () => {
    setIsMessageSending(true);
    const response = await dispatch(
      sendTicketMessage({
        message: `${message} ${defaultMessage}`,
        ticketNumber: subticket || id,
        files: attachments,
        users,
        notify: true,
        sentBy: user.email,
        fullName: `${user.firstName} ${user.lastName}`,
        userFirstName: user.firstName
      })
    )
    setIsMessageSending(false)
    if(response) {
      setMessage('');
      scrollToBottom();
      setAttachments([]);
    }
  }
  
  return (
    <Box>
      {suggestedResponses.length > 0 && !suggestedResponseUsed && (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            padding: '16px 36px 16px 12px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '8px',
            alignSelf: 'stretch',
            borderTop: '0.5px solid #C1C9D0',
            background: '#FFF',
            mb: 1,
          }}
        >
          <IconButton
            size="small"
            aria-label="Dismiss suggested responses"
            onClick={() => setSuggestedResponseUsed(true)}
            sx={{ position: 'absolute', top: 4, right: 4, px: 1, color: 'text.secondary' }}
          >
            <Iconify icon="eva:close-fill" width={16} />
          </IconButton>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {suggestedResponses.map((response, index) => (
              <Chip
                key={index}
                label={response.text}
                size="small"
                variant="outlined"
                onClick={() => {
                  setPrependText({ text: response.text, key: Date.now() });
                  setSuggestedResponseUsed(true);
                }}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  color: '#212B36',
                  border: '1px solid #DFE3E8',
                  borderRadius: '10px',
                  fontFamily: 'Public Sans, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  '& .MuiChip-label': { whiteSpace: 'normal', padding: '8px', display: 'block' },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'end',
          border: '2px solid #b4b4b4',
          borderRadius: '8px',
          padding: 0.5
        }}>
        {possibleUsers && (
          <MobileTicketInputEditor
            message={message}
            setMessage={setMessage}
            prependText={prependText}
          />
        )}
        <Box sx={{display: 'flex', mb: 0.3}}>
          <OptionsButton
            setIsTicketInfoOpen={setIsTicketInfoOpen}
            attachments={attachments}
            setAttachments={setAttachments}
            setIsAddingSubticket={setIsAddingSubticket}
          />
          <IconButton
            sx={{ width: 40, height: 28, minHeight: 28 }}
            color="primary"
            onClick={sendMessage}
            disabled={isMessageSending || (!message.trim() || message === "<p><br></p>") && !attachments?.length}
          >
            {isMessageSending ? (
              <CircularProgress size={20} color="primary" />
            ) : (
              <Iconify icon="mdi:send" />
            )}
          </IconButton>
        </Box>
      </Box>
      {attachments && attachments.length > 0 && <Grid container direction="row" alignItems="flex-start">
        {attachments.map((file) => (
          <FileAttachment
            key={file.name}
            file={file}
            fileName={file.name}
            canDeleteFile
            handleDeleteFile={() => removeAttachment(file)}
          />
        ))}
      </Grid>}
    </Box>
  );
}